/**
 * The standby peek (wave 7b-tail C): what the standby process currently
 * believes is on air and next, beside what the active shows — so an operator
 * can see that they agree before relying on a failover. They agree when they
 * hold the same part INSTANCES (the standby continues from the active's very
 * state), not merely the same titles.
 */
import type { StandbyPlayoutWire } from '@acp/api-contract/pairStatus.js';
import type { PlayoutStateWire } from '@acp/api-contract/playout.js';
import { agreement, beliefOf } from '../../lib/redundancy.js';
import { Badge } from '../ui/badge.js';

export type StandbyPeekProps = {
  /** The active's state, as the playout view holds it. */
  readonly active: PlayoutStateWire;
  /** The standby's view, or null before the first answer. */
  readonly standby: StandbyPlayoutWire | null;
  readonly error: string | null;
};

const VERDICT = {
  agree: { variant: 'success', text: 'They agree' },
  disagree: { variant: 'warning', text: 'They disagree: the standby is behind or diverged' },
  unknown: { variant: 'outline', text: 'Cannot compare' },
} as const;

function Line({ who, node, onAir, next }: { readonly who: string; readonly node: string; readonly onAir: string; readonly next: string }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-sm" data-testid={`peek-${who}`}>
      <span className="text-muted-foreground">
        {who} · <span className="font-mono">{node}</span>
      </span>
      <span>
        <span className="text-muted-foreground">on air </span>
        {onAir}
      </span>
      <span>
        <span className="text-muted-foreground">next </span>
        {next}
      </span>
    </div>
  );
}

export function StandbyPeek({ active, standby, error }: StandbyPeekProps) {
  const a = beliefOf(active);
  const b = standby?.state === null || standby?.state === undefined ? null : beliefOf(standby.state);
  const verdict = VERDICT[agreement(a, b)];
  return (
    <section className="grid gap-2 border-b bg-card px-4 py-2" aria-label="Standby peek" data-testid="standby-peek" data-agreement={agreement(a, b)}>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Standby peek</span>
        <Badge variant={verdict.variant}>{verdict.text}</Badge>
      </div>
      <Line who="active" node={active.redundancy?.node ?? 'this node'} onAir={a.onAir} next={a.next} />
      {b === null ? (
        <p className="text-sm text-muted-foreground" data-testid="peek-reason">
          standby: {error ?? standby?.reason ?? 'asking…'}
        </p>
      ) : (
        <Line who="standby" node={standby?.standbyNode ?? '?'} onAir={b.onAir} next={b.next} />
      )}
    </section>
  );
}
